import React from "react";
import { Tooltip } from "@material-ui/core";
import { css } from "@emotion/css";
import { QuestionnaireRollout, RevisionRollout, RolloutStatus } from "../api";
import { groupLabel } from "./RolloutStatusChip";
import { deployAttribution, isDeployTarget } from "../rolloutText";
import { Colors } from "../../../theme";

const statusColor = (status: RolloutStatus) => {
  if (status === "active") return Colors.SUCCESS;
  if (status === "activating") return Colors.JA_BLUE;
  if (status === "unpublishing") return Colors.WARNING_TEXT;
  return Colors.DISABLED;
};

const statusLabel = (status: RolloutStatus) => {
  if (status === "active") return "deployed";
  if (status === "activating") return "deploying";
  if (status === "unpublishing") return "undeploying";
  return "not deployed";
};

const questionnaireTooltip = (entry: QuestionnaireRollout) => {
  if (entry.status === "inactive") return `${groupLabel(entry.group)}: not deployed`;
  const target = entry.target_published_questionnaire_id ? ` #${entry.target_published_questionnaire_id}` : "";
  const status = entry.status === "active" ? "deployed" : "deploying";
  return `${groupLabel(entry.group)}:${target} ${status}${
    isDeployTarget(entry.status) ? ` · ${deployAttribution(entry)}` : ""
  }`;
};

const revisionTooltip = (entry: RevisionRollout) =>
  `${groupLabel(entry.group)}: ${statusLabel(entry.status)}${
    isDeployTarget(entry.status) ? ` · ${deployAttribution(entry)}` : ""
  }`;

// Color alone isn't enough to tell "deployed" from "deploying" from "undeploying" at a glance
// (and fails for colorblind readers), so each status also gets a distinct shape/motion treatment:
// deployed = solid fill, deploying = pulsing ring (arriving), undeploying = dashed + fading (leaving).
const badgeVariant = (status: RolloutStatus) => {
  if (status === "active") return styles.badgeDeployed;
  if (status === "activating") return styles.badgeDeploying;
  if (status === "unpublishing") return styles.badgeUndeploying;
  return styles.badgeMissing;
};

const Badge: React.VFC<{ group: string; status: RolloutStatus; tooltip: string }> = ({ group, status, tooltip }) => (
  <Tooltip title={tooltip}>
    <span
      className={`${styles.badge} ${badgeVariant(status)}`}
      style={
        status === "active"
          ? { backgroundColor: statusColor(status), borderColor: statusColor(status) }
          : { borderColor: statusColor(status), color: statusColor(status) }
      }
    >
      <span className={styles.glyph}>{groupLabel(group).charAt(0).toUpperCase()}</span>
    </span>
  </Tooltip>
);

export const QuestionnaireRolloutBadges: React.VFC<{ rollout: QuestionnaireRollout[] }> = ({ rollout }) => (
  <span className={styles.row}>
    {rollout
      .filter(entry => entry.status !== "inactive")
      .map(entry => (
        <Badge key={entry.group} group={entry.group} status={entry.status} tooltip={questionnaireTooltip(entry)} />
      ))}
  </span>
);

export const RevisionRolloutBadges: React.VFC<{ rollout: RevisionRollout[] }> = ({ rollout }) => (
  <span className={styles.row}>
    {rollout
      .filter(entry => entry.status !== "inactive")
      .map(entry => (
        <Badge key={entry.group} group={entry.group} status={entry.status} tooltip={revisionTooltip(entry)} />
      ))}
  </span>
);

const styles = {
  row: css`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: 6px;
  `,
  badge: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1.5px solid;
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1;
  `,
  // Optical correction: this font's caps sit ascent-heavy within the line box (little to no
  // descender for S/P), so centering the line box via flex leaves the ink ~1px high. Nudge the
  // glyph itself, not the circle, so the border stays exactly centered.
  glyph: css`
    display: inline-block;
    transform: translateY(1px);
  `,
  badgeDeployed: css`
    color: #fff;
  `,
  badgeDeploying: css`
    animation: deploying-pulse 1.6s ease-out infinite;
    @keyframes deploying-pulse {
      0% {
        box-shadow: 0 0 0 0 currentColor;
      }
      70% {
        box-shadow: 0 0 0 4px transparent;
      }
      100% {
        box-shadow: 0 0 0 0 transparent;
      }
    }
  `,
  badgeUndeploying: css`
    border-style: dashed;
    animation: undeploying-fade 2.2s ease-in-out infinite;
    @keyframes undeploying-fade {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.45;
      }
    }
  `,
  badgeMissing: css``,
};
